
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { Edit, Trash2, Bed, Coffee, Moon, Sun, Baby, Milk, MoreVertical, Layers } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from '@/components/ui/button';
import { SleepLogDialog } from '@/components/sleep-log-dialog';
import type { SleepEvent, NewSleepEvent, SleepEventType, SleepGoal } from '@/lib/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';

type LogTabProps = {
  events: SleepEvent[];
  childId?: string;
  onEditEvent: (eventId: string, data: Omit<NewSleepEvent, 'childId'>) => void;
  onDeleteEvent: (eventId: string) => void;
  onAddEvent?: (data: Omit<NewSleepEvent, 'childId'>) => void;
  onAddEventsBatch?: (events: Omit<NewSleepEvent, 'childId'>[]) => void;
  disabled?: boolean;
  goal?: SleepGoal;
}

const PAGE_SIZE = 50;

const eventTypeDetails: { [key in SleepEventType]: { label: string, icon: ReactNode } } = {
  bedtime: { label: 'Bedtime', icon: <Moon className="h-4 w-4 text-blue-500" /> },
  wake: { label: 'Wake Up', icon: <Sun className="h-4 w-4 text-yellow-500" /> },
  nap: { label: 'Nap', icon: <Bed className="h-4 w-4 text-purple-500" /> },
  nap_start: { label: 'Nap Start', icon: <Bed className="h-4 w-4 text-purple-500" /> },
  nap_end: { label: 'Nap End', icon: <Coffee className="h-4 w-4 text-orange-500" /> },
  night_wake: { label: 'Night Wake', icon: <Baby className="h-4 w-4 text-red-500" /> },
  feed: { label: 'Feed', icon: <Milk className="h-4 w-4 text-green-500" /> },
};

export function LogTab({ events, childId, onEditEvent, onDeleteEvent, onAddEvent, onAddEventsBatch, disabled, goal }: LogTabProps) {
  const [eventToEdit, setEventToEdit] = useState<SleepEvent | undefined>(undefined);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isBatchDialogOpen, setIsBatchDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<SleepEvent | undefined>(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const prevChildIdRef = useRef<string | undefined>();

  // Ensure LogTab always starts at the top when navigated to
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
  }, []);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => b.time.toMillis() - a.time.toMillis());
  }, [events]);

  useEffect(() => {
    if (prevChildIdRef.current !== childId) {
      setVisibleCount(PAGE_SIZE);
      prevChildIdRef.current = childId;
    }
  }, [childId]);

  useEffect(() => {
    setVisibleCount(prev => {
      if (sortedEvents.length === 0) {
        return PAGE_SIZE;
      }

      if (prev === 0) {
        return Math.min(PAGE_SIZE, sortedEvents.length);
      }

      if (prev > sortedEvents.length) {
        return sortedEvents.length;
      }

      return prev;
    });
  }, [sortedEvents.length]);

  const hasMoreEvents = visibleCount < sortedEvents.length;

  const loadMoreEvents = useCallback(() => {
    if (!hasMoreEvents) {
      return;
    }

    setVisibleCount(prev => {
      if (prev >= sortedEvents.length) {
        return prev;
      }

      return Math.min(prev + PAGE_SIZE, sortedEvents.length);
    });
  }, [hasMoreEvents, sortedEvents.length]);

  useEffect(() => {
    if (!hasMoreEvents) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadMoreEvents();
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreEvents, loadMoreEvents]);

  const displayEvents = useMemo(() => {
    return sortedEvents.slice(0, visibleCount);
  }, [sortedEvents, visibleCount]);

  const handleEditClick = (event: SleepEvent) => {
    setEventToEdit(event);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (event: SleepEvent) => {
    setEventToDelete(event);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (eventToDelete) {
      onDeleteEvent(eventToDelete.id);
      setIsDeleteDialogOpen(false);
      setEventToDelete(undefined);
    }
  };

  const getValidEventDate = (event: SleepEvent): Date | null => {
    if (!event.time) return null;
    try {
      const eventDate = (event.time as Timestamp).toDate();
      if (isNaN(eventDate.getTime())) {
        console.warn('Invalid date for event:', event);
        return null;
      }
      return eventDate;
    } catch (error) {
      console.warn('Error converting timestamp for event:', event, error);
      return null;
    }
  };

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 max-w-full overflow-x-hidden">
        <Card className="overflow-hidden">
            <CardHeader className="p-4 sm:p-6 flex flex-row items-center justify-between gap-2 space-y-0">
                <div>
                    <CardTitle>Event Log</CardTitle>
                    <CardDescription>A complete history of all logged events.</CardDescription>
                </div>
                {onAddEvent && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsBatchDialogOpen(true)}
                        className="h-8 text-xs font-medium gap-1.5 touch-manipulation flex-shrink-0"
                        disabled={disabled || !childId}
                    >
                        <Layers className="h-3.5 w-3.5 text-primary" />
                        <span>Catch Up on Naps</span>
                    </Button>
                )}
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
                {displayEvents.length === 0 ? (
                    <div className="text-center py-12 text-sm text-muted-foreground">
                        No events logged yet.
                    </div>
                ) : (
                    <>
                        {/* Mobile list view: responsive vertical layout that eliminates horizontal scrolling */}
                        <div className="divide-y divide-border/60 md:hidden">
                            {displayEvents.map(event => {
                                const eventDate = getValidEventDate(event);
                                if (!eventDate) return null;

                                return (
                                    <div
                                        key={event.id}
                                        className="flex items-start justify-between gap-3 py-3"
                                    >
                                        <div className="flex items-start gap-3 min-w-0 flex-1">
                                            <div className="flex-shrink-0 mt-0.5 p-2 rounded-full bg-secondary/50 text-foreground">
                                                {eventTypeDetails[event.type]?.icon}
                                            </div>
                                            <div className="min-w-0 flex-1 space-y-0.5">
                                                <div className="flex items-baseline justify-between gap-2">
                                                    <span className="font-semibold text-sm truncate">
                                                        {eventTypeDetails[event.type]?.label || 'Event'}
                                                    </span>
                                                    <span className="text-sm font-semibold tabular-nums flex-shrink-0">
                                                        {format(eventDate, 'HH:mm')}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                                                    <span>{format(eventDate, 'MMM d, yyyy')}</span>
                                                    {event.type === 'night_wake' && event.duration && (
                                                        <span className="font-medium text-foreground/80">
                                                            Duration: {event.duration}m
                                                        </span>
                                                    )}
                                                </div>
                                                {event.notes && (
                                                    <p className="text-xs text-muted-foreground italic break-words pt-0.5">
                                                        "{event.notes}"
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-foreground -mr-1"
                                                    disabled={disabled}
                                                    aria-label="Event actions"
                                                >
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onSelect={() => handleEditClick(event)}>
                                                    <Edit className="mr-2 h-4 w-4" /> Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onSelect={() => handleDeleteClick(event)} className="text-destructive">
                                                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop table view */}
                        <div className="hidden md:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Time</TableHead>
                                        <TableHead>Details</TableHead>
                                        <TableHead className="text-right"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {displayEvents.map(event => {
                                        const eventDate = getValidEventDate(event);
                                        if (!eventDate) return null;

                                        return (
                                            <TableRow key={event.id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {eventTypeDetails[event.type]?.icon}
                                                        <span className="font-medium">{eventTypeDetails[event.type]?.label}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col">
                                                        <span>{format(eventDate, 'HH:mm')}</span>
                                                        <span className="text-xs text-muted-foreground">{format(eventDate, 'MMM d, yyyy')}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col">
                                                        {event.type === 'night_wake' && event.duration && (
                                                            <span className="text-xs">Duration: {event.duration}m</span>
                                                        )}
                                                        {event.notes && <span className="text-xs text-muted-foreground italic">"{event.notes}"</span>}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" disabled={disabled} aria-label="Event actions">
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onSelect={() => handleEditClick(event)}>
                                                                <Edit className="mr-2 h-4 w-4" /> Edit
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onSelect={() => handleDeleteClick(event)} className="text-destructive">
                                                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}

                {hasMoreEvents && (
                    <div className="flex flex-col items-center gap-2 pt-4 pb-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadMoreEvents}
                            disabled={!hasMoreEvents}
                        >
                            Load more events
                        </Button>
                        <div
                            ref={sentinelRef}
                            aria-hidden="true"
                            className="h-1 w-full"
                        />
                    </div>
                )}
            </CardContent>
        </Card>

      {/* Edit Dialog */}
      <SleepLogDialog 
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSave={() => {}} // This won't be called from here
        onEdit={(eventId, data) => {
            onEditEvent(eventId, data);
            setEventToEdit(undefined);
        }}
        disabled={disabled}
        goal={goal}
        eventToEdit={eventToEdit}
      >
        {/* This dialog is controlled programmatically, so it doesn't need a trigger child. */}
      </SleepLogDialog>

      {/* Batch Catch Up Dialog */}
      {onAddEvent && (
        <SleepLogDialog
          open={isBatchDialogOpen}
          onOpenChange={setIsBatchDialogOpen}
          onSave={onAddEvent}
          onSaveBatch={onAddEventsBatch}
          initialMode="batch"
          disabled={disabled}
          goal={goal}
        />
      )}

      {/* Delete Confirmation Dialog */}
       <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete this event from your log.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setEventToDelete(undefined)}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </div>
  );
}
